// DarkMapBare.jsx
import { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useSecret } from '../../stores/secretsStore';

/**
 * Lazily-load the Google Maps script **once** and
 * return a promise that resolves to `google.maps`.
 * @type {Promise<typeof google.maps>|null}
 */
let googleMapsPromise = null;

/**
 * Inject the Maps script tag the first time we need it.
 * Subsequent calls reuse the same promise.
 * @param {string} apiKey
 * @returns {Promise<typeof google.maps>}
 */
function loadGoogleMaps(apiKey) {
  if (googleMapsPromise) return googleMapsPromise;

  googleMapsPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}`;
    script.async = true;
    script.onerror = reject;
    script.onload = () => {
      if (window.google && window.google.maps) {
        resolve(window.google.maps);
      } else {
        reject(new Error('Google Maps failed to load'));
      }
    };
    document.head.appendChild(script);
  });

  return googleMapsPromise;
}

/** @type {google.maps.MapTypeStyle[]} Night-mode style array */
const darkStyle = [
  { elementType: 'geometry',           stylers: [{ color: '#242f3e' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#242f3e' }] },
  { elementType: 'labels.text.fill',   stylers: [{ color: '#746855' }] },
  { featureType: 'administrative.locality', elementType: 'labels.text.fill', stylers: [{ color: '#d59563' }] },
  { featureType: 'poi', elementType: 'labels.text.fill',  stylers: [{ color: '#d59563' }] },
  { featureType: 'poi.park', elementType: 'geometry',     stylers: [{ color: '#263c3f' }] },
  { featureType: 'poi.park', elementType: 'labels.text.fill', stylers: [{ color: '#6b9a76' }] },
  { featureType: 'road', elementType: 'geometry',         stylers: [{ color: '#38414e' }] },
  { featureType: 'road', elementType: 'geometry.stroke',  stylers: [{ color: '#212a37' }] },
  { featureType: 'road', elementType: 'labels.text.fill', stylers: [{ color: '#9ca5b3' }] },
  { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#746855' }] },
  { featureType: 'road.highway', elementType: 'geometry.stroke', stylers: [{ color: '#1f2835' }] },
  { featureType: 'road.highway', elementType: 'labels.text.fill', stylers: [{ color: '#f3d19c' }] },
  { featureType: 'transit', elementType: 'geometry',      stylers: [{ color: '#2f3948' }] },
  { featureType: 'transit.station', elementType: 'labels.text.fill', stylers: [{ color: '#d59563' }] },
  { featureType: 'water', elementType: 'geometry',        stylers: [{ color: '#17263c' }] },
  { featureType: 'water', elementType: 'labels.text.fill',   stylers: [{ color: '#515c6d' }] },
  { featureType: 'water', elementType: 'labels.text.stroke', stylers: [{ color: '#17263c' }] }
];

/**
 * Displays a dark-themed Google Map with a single marker.
 *
 * @component
 * @param {Object} props
 * @param {number} props.lat   Latitude to center/mark.
 * @param {number} props.lng   Longitude to center/mark.
 * @param {number} [props.zoom=15]  Initial zoom level.
 * @param {React.CSSProperties} [props.style] Inline style overrides for the container.
 */
export default function GoogleMapContainer({ lat, lng, zoom = 15, style }) {
  const containerRef = useRef(null);
  const apiKey = useSecret('GOOGLE_MAPS_API_KEY');

  useEffect(() => {
    if (!apiKey) return;
    /** @type {google.maps.Map|undefined} */
    let map;

    loadGoogleMaps(apiKey)
      .then((gmaps) => {
        if (!containerRef.current) return;

        // Create the map
        map = new gmaps.Map(containerRef.current, {
          center: { lat, lng },
          zoom,
          styles: darkStyle,
          disableDefaultUI: true,
          gestureHandling: 'greedy'
        });

        // Add a single marker
        new gmaps.Marker({ position: { lat, lng }, map });
      })
      .catch(console.error);

    return () => {
      // optional cleanup—remove references so GC can collect
      map = undefined;
    };
  }, [lat, lng, zoom, apiKey]);

  return (
    <div
      ref={containerRef}
      style={{
        width: '100%',
        height: 400,
        ...style
      }}
    />
  );
}

GoogleMapContainer.propTypes = {
  /** Latitude coordinate */
  lat: PropTypes.number.isRequired,
  /** Longitude coordinate */
  lng: PropTypes.number.isRequired,
  /** Zoom level (default 15) */
  zoom: PropTypes.number,
  /** Optional inline styles for the map container */
  style: PropTypes.object
};
